import { BUSINESS_LINE_OPTIONS, SelectValuesInterface } from './../../../../shared/models/businessLine-model';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FHService, ModalService } from 'npx-family-happy-common';
import { ServiceServices } from '../../service-services/service.services';
import { Subject, takeUntil } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { TextEditorComponent } from 'src/app/shared/components/text-editor/text-editor.component';
import { B2BCompanySelectComponent } from 'src/app/shared/components/custom-select/b2b-company-select/b2b-company-select.component';
import { B2BCompany } from 'npx-family-happy-common';

interface ServiceWithExcluded extends FHService {
  excluded?: boolean;
}

/**
 * When the customer type is B2B a resolved company (an object with an id) is mandatory.
 * This guards against the typeahead leaving a free-text value that never got linked.
 */
function b2bCompanyRequiredForB2B(group: AbstractControl): ValidationErrors | null {
  const customerType = group.get('customerType')?.value;
  if (customerType !== 'B2B') {
    return null;
  }
  const company = group.get('b2bCompany')?.value;
  const resolved = !!company && typeof company === 'object' && !!company.id;
  return resolved ? null : { b2bCompanyRequired: true };
}

@Component({
  selector: 'app-service-detail',
  templateUrl: './service-detail.component.html',
  styleUrls: ['./service-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AlertComponent, TextEditorComponent, B2BCompanySelectComponent]
})
export class ServiceDetailComponent implements OnInit {
  form = new UntypedFormGroup({
    businessLine: new UntypedFormControl(null, [Validators.required]),
    customerType: new UntypedFormControl(null, [Validators.required]),
    id: new UntypedFormControl(null),
    name: new UntypedFormControl(null, [Validators.required]),
    script: new UntypedFormControl(null),
    disabled: new UntypedFormControl(null),
    b2bCompany: new UntypedFormControl(null),
    excluded: new UntypedFormControl(false)
  }, { validators: [b2bCompanyRequiredForB2B] });

  isNew: boolean = false;
  showDeleteConfirm: boolean = false;
  selectedService: ServiceWithExcluded | undefined;
  scriptText!: string;
  editorTitle = "Script dei Servizi"
  localVariable = ''
  formInvalid = false
  isDirty = false
  haveText = false;
  /** True while the B2B company typeahead holds text that was not picked from the dropdown. */
  b2bSelectionInvalid = false;
  selectValue!:Array<SelectValuesInterface>

  private unsubscribe = new Subject<void>();

  constructor(
    private serviceServices: ServiceServices,
    private modalService: ModalService,
    private router: Router,
    private activeRoute: ActivatedRoute
  ) {
    this.selectValue = BUSINESS_LINE_OPTIONS;
  }

  ngOnInit(): void {
    this.activeRoute.paramMap
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((param) => {
        const id = param.get('id') ?? -1;
        if (id === 'create') {
          this.isNew = true;
        } else {
          if (id === -1) {
            this.goBackToService();
          } else {
            this.serviceServices
              .getServiceDetailsByID(+id)
              .subscribe((service: ServiceWithExcluded) => {
                this.selectedService = service;
                this.initForm(service);
              });
          }
        }
      });
  }

  saveService() {
    if (this.form.valid && !this.b2bSelectionInvalid) {
      const formValue = this.form.value;
      const service: ServiceWithExcluded = {
        ...formValue,
        script: this.normalizeScript(formValue.script),
      };
      this.serviceServices
        .saveService(service)
        .subscribe({
          next: () =>
            this.modalService.showSuccessMessage(
              'Servizio registrato con successo'
            ),
          error: (error) =>
            this.modalService.showErrorMessage(
              this.buildErrorMessage(error, 'Impossibile salvare il servizio.')
            ),
        });
    } else {
      this.isDirty = true
      this.haveText = false
      this.formInvalid = true
      this.form.markAllAsTouched()
    }
  }

  deleteService() {
    this.showDeleteConfirm = true
  }

  confirmDelete(answer: boolean) {
    if (answer && this.selectedService && this.selectedService.id > -1) {
      this.serviceServices.deleteServiceById(this.selectedService.id).subscribe({
        next: () => {
          this.modalService.showSuccessMessage('Servizio eliminato');
          this.goBackToService();
        },
        error: (error) =>
          this.modalService.showErrorMessage(
            this.buildErrorMessage(error, 'Impossibile eliminare il servizio.')
          ),
      });
    }
    this.showDeleteConfirm = false;
  }

  validatorErrorMessage(fieldName: string) {
    const field = this.form.get(fieldName);
    let message = ''
    if (field?.errors) {
      if (field?.errors['required']) {
        message = `Questo campo è obbligatorio`
      }
    }
    return message
  }

  goBackToService() {
    this.router.navigate(['/services']);
  }

  saveScript(event: string) {
    this.isDirty = true
    this.haveText = true
    this.form.controls.script.setValue(event)
  }

  updateB2BCompany(company: B2BCompany | undefined) {
    if (company) {
      this.form.controls.customerType.setValue('B2B');
    }
    this.form.controls.b2bCompany.setValue(company ?? null);
    this.form.updateValueAndValidity();
  }

  onB2BSelectionInvalid(invalid: boolean) {
    this.b2bSelectionInvalid = invalid;
  }

  initForm(service: ServiceWithExcluded) {
    this.form.setValue({
      businessLine: service.businessLine,
      customerType: service.customerType,
      id: service.id,
      name: service.name,
      script: service.script,
      disabled: service.disabled,
      b2bCompany: service.b2bCompany,
      excluded: service.excluded ?? false
    });
    this.haveText = true
    this.scriptText = this.form.controls.script.value;
  }

  /** Quill emits `<p><br></p>` (and similar) for visually empty content: treat it as empty. */
  private normalizeScript(script: string | null | undefined): string {
    if (!script) {
      return '';
    }
    const stripped = script.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return stripped.length === 0 ? '' : script;
  }

  private buildErrorMessage(error: unknown, fallback: string): string {
    const backendMessage = (error as any)?.error?.message ?? (error as any)?.message;
    return backendMessage ? `${fallback} (${backendMessage})` : fallback;
  }
}
